import { useEffect, useRef, useState } from 'react';

export interface ResponsiveImageProps
  extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'alt' | 'loading' | 'decoding'> {
  src: string;
  alt: string;
  /** Tiny blurred preview (data URI) shown until the real image has loaded. */
  lqip?: string | null;
  /** Set for above-the-fold images (e.g. the LCP image) to load eagerly. */
  priority?: boolean;
}

/**
 * Base image for anything we control the variants of (Sanity images, generated local
 * images). Pass `srcSet` + `sizes` so the browser downloads the smallest adequate file,
 * and `width`/`height` so the layout space is reserved before the image arrives.
 *
 * - Defers off-screen images (`loading="lazy"`) and decodes off the main thread.
 * - Shows the blurred `lqip` as a background until the image has loaded.
 */
const ResponsiveImage = ({
  src,
  alt,
  lqip,
  priority = false,
  style,
  onLoad,
  ...imgProps
}: ResponsiveImageProps) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const [loaded, setLoaded] = useState(false);

  // A cached image can finish loading before React attaches `onLoad`.
  useEffect(() => {
    const element = imgRef.current;
    setLoaded(Boolean(element?.complete && element.naturalWidth > 0));
  }, [src]);

  return (
    <img
      {...imgProps}
      ref={imgRef}
      src={src}
      alt={alt}
      loading={priority ? 'eager' : 'lazy'}
      decoding="async"
      onLoad={(event) => {
        setLoaded(true);
        onLoad?.(event);
      }}
      style={{
        ...(lqip && !loaded ? { backgroundImage: `url(${lqip})`, backgroundSize: 'cover' } : undefined),
        ...style,
      }}
    />
  );
};

export default ResponsiveImage;
